import DeleteIcon from "@mui/icons-material/Delete";
import GitHubIcon from "@mui/icons-material/GitHub";
import GitLabIcon from "@mui/icons-material/GitHub"; // Reuse GitHub icon for GitLab placeholder
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { VcsConnectionSummary } from "@generated/vcs-connections";

import { VcsActionButtons } from "./GitHubAppInstallCard";

const STATUS_COLOR: Record<string, "success" | "error" | "default"> = {
  connected: "success",
  disconnected: "default",
  error: "error",
};

interface VcsConnectionListProps {
  connections: VcsConnectionSummary[];
  loading: boolean;
  installing: boolean;
  gitLabAuthing: boolean;
  polling: boolean;
  hasActiveGithub: boolean;
  hasActiveGitLab: boolean;
  onInstall: () => void;
  onGitLabAuth: () => void;
  onDisconnect: (id: number) => void;
}

export function VcsConnectionList({
  connections,
  loading,
  installing,
  gitLabAuthing,
  polling,
  hasActiveGithub,
  hasActiveGitLab,
  onInstall,
  onGitLabAuth,
  onDisconnect,
}: VcsConnectionListProps) {
  const hasConnections = connections.length > 0;

  const actionButtonsProps = {
    installing,
    gitLabAuthing,
    polling,
    hasActiveGithub,
    hasActiveGitLab,
    onInstall,
    onGitLabAuth,
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasConnections) {
    // Empty state is rendered by the parent via GitHubAppInstallCard; return null here.
    return null;
  }

  return (
    <Stack spacing={2}>
      {connections.map((conn) => (
        <Card key={conn.id} variant="outlined">
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {conn.provider === "github" ? (
              <GitHubIcon color="action" />
            ) : (
              <GitLabIcon color="action" />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600}>{conn.providerOrgName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {conn.provider === "github" ? "GitHub App" : "GitLab OAuth"} ·
                Connected {new Date(conn.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            <Chip
              label={conn.status}
              size="small"
              color={STATUS_COLOR[conn.status] ?? "default"}
            />
            <Tooltip title="Disconnect">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDisconnect(conn.id)}
                aria-label="disconnect"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </CardContent>
        </Card>
      ))}

      {/* Add another org */}
      {!polling && !gitLabAuthing && (
        <VcsActionButtons {...actionButtonsProps} variant="inline" />
      )}
    </Stack>
  );
}
